import { useState, useMemo } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import toast from "react-hot-toast";
import PostEventForm from "../../components/post-event/event-form";
import { sampleEvents } from "./sample-events";
import { BASE_URL, API_ENDPOINTS } from "../../config/api.config";
import { handleAuthError } from "../../utils/authUtils";

function EditEvent() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [conflictModal, setConflictModal] = useState(false);
  const [conflictData, setConflictData] = useState([]);
  const [pendingSubmit, setPendingSubmit] = useState(null); // { formValues, isDraft }

  const event = useMemo(() => {
    if (location.state?.event) {
      return location.state.event;
    }

    return sampleEvents.find((item) => item.id === id) ?? null;
  }, [id, location.state]);

  const createPayload = (formValues, isDraft = false) => {
    if (!id && !event?.id) {
      return null;
    }

    const eventId = id || event?.id;

    // Send attachments as objects with url and name, or strings for backward compatibility
    const attachments = Array.isArray(formValues.attachments)
      ? formValues.attachments
          .map((att) => {
            // If it's already an object with url and name, use it as-is
            if (att && typeof att === "object" && att.url) {
              return {
                url: att.url,
                name: att.name || att.url.split("/").pop() || "file",
              };
            }
            // If it's a string (URL), convert to object with extracted filename
            if (typeof att === "string") {
              return {
                url: att,
                name: att.split("/").pop() || "file",
              };
            }
            return null;
          })
          .filter(Boolean)
      : [];

    return {
      eventId,
      title: formValues.title?.trim() ?? "",
      categoryValue: formValues.categoryValue || undefined, // Use categoryValue instead of typeCode
      description: formValues.description?.trim() || undefined,
      date: formValues.date,
      time: formValues.time,
      location: formValues.location?.trim() ?? "",
      latitude: formValues.latitude ?? undefined,
      longitude: formValues.longitude ?? undefined,
      zipCode: formValues.zipCode || undefined,
      resourceRequirements: formValues.resourceRequirements?.trim() || undefined,
      needVolunteers: !!formValues.needVolunteers,
      inviteNgoMunicipality: !!formValues.inviteNgoMunicipality,
      roles: Array.isArray(formValues.roles)
        ? formValues.roles.map((role) => ({
            roleName: role.roleName,
            countNeeded: Number(role.countNeeded) || 0,
            specialRequirements: role.specialRequirements || undefined,
          }))
        : [],
      attachments,
      coverImageUrl: formValues.coverImageUrl || undefined,
      isDraft: isDraft || formValues.isDraft || false,
    };
  };

  const doSubmit = async (formValues, isDraft, forceCreate = false) => {
    const token = localStorage.getItem("accessToken");
    if (!token) {
      toast.error("Session expired. Please sign in again.");
      navigate("/");
      return;
    }

    if (!id && !event?.id) {
      toast.error("Event ID is missing. Cannot update event.");
      return;
    }

    const payload = createPayload(formValues, isDraft);
    if (!payload) return;
    if (forceCreate) payload.forceCreate = true;

    setIsSubmitting(true);
    try {
      const response = await fetch(
        `${BASE_URL}${API_ENDPOINTS.NONPROFIT_UPDATE_EVENT}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        }
      );

      const result = await response.json().catch(() => null);
      const statusCode = result?.statusCode ?? response.status;

      if (handleAuthError(result, response.status, dispatch)) return;

      if (response.status === 409 && result?.conflicts) {
        setConflictData(result.conflicts);
        setPendingSubmit({ formValues, isDraft });
        setConflictModal(true);
        return;
      }

      if (!response.ok || statusCode >= 400) {
        throw new Error(result?.message || (isDraft ? "Unable to save draft." : "Unable to update event."));
      }

      toast.success(isDraft ? "Draft saved successfully." : "Event updated successfully.");
      navigate("/dashboard/manage-events", { replace: true, state: { refresh: true } });
    } catch (error) {
      console.error("Update event error:", error);
      toast.error(error?.message || (isDraft ? "Unable to save draft." : "Unable to update event."));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = (formValues) => doSubmit(formValues, false);
  const handleSaveDraft = (formValues) => doSubmit(formValues, true);

  if (!event) {
    return (
      <div className="w-[90%] lg:w-[95%] mx-auto my-12">
        <p className="font-popinsMedium text-lg text-black/60">
          We couldn't locate the event you are trying to edit.
        </p>
      </div>
    );
  }

  return (
    <>
    <PostEventForm
      mode="edit"
      submitLabel="Save Changes"
      isSubmitting={isSubmitting}
      eventId={id || event?.id}
      initialValues={{
        title: event.title,
        typeCode: event.categoryValue || event.typeCode, // Use categoryValue if available, fallback to typeCode
        description: event.description,
        date: event.date,
        time: event.time,
        location: event.location,
        latitude: event.latitude ?? null,
        longitude: event.longitude ?? null,
        zipCode: event.zipCode || "",
        resourceRequirements: event.resourceRequirements,
        needVolunteers: event.needVolunteers,
        inviteNgoMunicipality: event.inviteNgoMunicipality,
        coverImageUrl: event.coverImageUrl,
        attachments: event.attachments || [],
        id: id || event?.id,
      }}
      initialRoles={event.roles}
      onSubmit={handleSubmit}
      onSaveDraft={handleSaveDraft}
    />

    {conflictModal && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
        <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg mx-4 p-6">
          <h2 className="flex items-center justify-center gap-2 font-popinsSemiBold text-lg text-gray-900 mb-2">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-yellow-400 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor">
              <path fillRule="evenodd" d="M9.401 3.003c1.155-2 4.043-2 5.197 0l7.355 12.748c1.154 2-.29 4.5-2.599 4.5H4.645c-2.309 0-3.752-2.5-2.598-4.5L9.4 3.003zM12 8.25a.75.75 0 01.75.75v3.75a.75.75 0 01-1.5 0V9a.75.75 0 01.75-.75zm0 8.25a.75.75 0 100-1.5.75.75 0 000 1.5z" clipRule="evenodd" />
            </svg>
            Warning
          </h2>
          <p className="font-popinsRegular text-sm text-gray-600 mb-4">
            The selected date{" "}
            {pendingSubmit?.formValues?.date && (
              <span className="font-popinsSemiBold text-gray-900">
                {pendingSubmit.formValues.date.slice(5,7)}/{pendingSubmit.formValues.date.slice(8,10)}/{pendingSubmit.formValues.date.slice(0,4)}
              </span>
            )}{" "}
            already has the following event scheduled by NGOs:
          </p>
          <ul className="mb-5 space-y-4 max-h-[420px] overflow-y-auto">
            {conflictData.map((c, i) => {
              const formatted = c.date
                ? `${c.date.slice(5,7)}/${c.date.slice(8,10)}/${c.date.slice(0,4)} ${c.time}`
                : c.time;
              return (
                <li key={i} className="border border-gray-200 rounded-lg p-3">
                  <div className="flex items-center">
                    <div className="w-[50%]">
                      {c.coverImageUrl ? (
                        <img src={c.coverImageUrl} alt="Event cover preview" className="w-full h-[150px] object-cover rounded-lg" />
                      ) : (
                        <div className="w-full h-[150px] bg-gray-100 rounded-lg flex items-center justify-center">
                          <p className="text-gray-400 text-sm text-center px-4">No image available</p>
                        </div>
                      )}
                    </div>
                    <div className="w-[50%] pl-5">
                      <p className="font-popinsMedium text-base">{c.title}</p>
                      <p className="font-popinsRegular text-sm">{c.organizationName}</p>
                      <p className="font-popinsRegular text-sm text-gray-500">{formatted}</p>
                      {c.description && (
                        <p className="font-popinsRegular text-sm text-gray-500 mt-1 line-clamp-2">{c.description}</p>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
          <div className="flex gap-3 justify-end">
            <button
              onClick={() => { setConflictModal(false); setPendingSubmit(null); }}
              className="px-4 py-2 rounded-lg border border-gray-300 font-popinsMedium text-sm text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                setConflictModal(false);
                doSubmit(pendingSubmit.formValues, pendingSubmit.isDraft, true);
              }}
              className="px-4 py-2 rounded-lg bg-red-600 font-popinsMedium text-sm text-white hover:bg-red-700"
            >
              Update Anyway
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
}

export default EditEvent;
